import React, { useCallback, useEffect, useId, useImperativeHandle, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Calendar as CalendarLucide } from 'lucide-react'
import { Button } from './Button'
import { Calendar } from './Calendar'
import { DateField } from './DateField'
import { isTopmostOverlay } from './overlayStack'
import { formatDateYMD, parseDateYMD } from 'utils/format-date'

export interface DatePickerProps {
    variant?: 'primary' | 'secondary'
    label?: string
    description?: string
    error?: string
    value?: Date | null
    defaultValue?: Date | null
    onChange?: (date: Date | null) => void
    disabled?: boolean
    required?: boolean
    minDate?: Date
    maxDate?: Date
    /** The date can also be typed (mm / dd / yyyy) next to a calendar button, instead of only picked; an impossible day such as 02/31 is refused inline. */
    typeable?: boolean
    placement?: 'auto' | 'above' | 'below'
    style?: React.CSSProperties
    /** Names the field when there is no visible `label` (filters, table cells). */
    'aria-label'?: string
    /**
     * With `typeable`: the calendar icon sits inside the field and opens the browser's own date popup, like a native date input. Only the typed
     * text is ours (always mm/dd/yyyy); the popup still follows the browser language, which is harmless because a day is picked by clicking it.
     */
    nativeCalendar?: boolean
    /** Lets a form move the cursor into the field, e.g. to the first invalid one on submit. */
    focusRef?: React.Ref<{ focus: () => void }>
    /**
     * Focus moved out of the field, its calendar button and its calendar, with the value it holds now (save-on-blur editors, Formik `touched`).
     * `incomplete` is true when something is typed but it is not an accepted date (half-typed, 02/31, outside min/max): the value is then null
     * like a full clear, and a save-on-blur editor should put the stored date back instead of saving a clear.
     */
    onBlur?: (date: Date | null, state: { incomplete: boolean }) => void
}

const MIN_TYPED_YEAR = 1900

interface DateSegments {
    month: number | null
    day: number | null
    year: number | null
}

const toSegments = (date: Date | null | undefined): DateSegments => ({
    month: date ? date.getMonth() + 1 : null,
    day: date ? date.getDate() : null,
    year: date ? date.getFullYear() : null,
})

/** The typed segments as a Date: only a real calendar day (no 02/31) with a four-digit year counts, anything else is null. */
const segmentsToDate = ({ month, day, year }: DateSegments): Date | null => {
    if (month === null || day === null || year === null || year < MIN_TYPED_YEAR) return null
    const date = new Date(year, month - 1, day)
    return date.getMonth() === month - 1 && date.getDate() === day ? date : null
}

const isSegmentsTouched = ({ month, day, year }: DateSegments): boolean => month !== null || day !== null || year !== null

const isSegmentsComplete = ({ month, day, year }: DateSegments): boolean => month !== null && day !== null && year !== null && String(year).length === 4

const isSameDay = (a: Date | null | undefined, b: Date | null | undefined): boolean =>
    a === b || (!!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate())

const startOfDay = (date: Date): number => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

const isOutOfRange = (date: Date, minDate?: Date, maxDate?: Date): boolean =>
    (!!minDate && startOfDay(date) < startOfDay(minDate)) || (!!maxDate && startOfDay(date) > startOfDay(maxDate))

const pad2 = (n: number): string => String(n).padStart(2, '0')

/** Always US order, whatever the browser language is: native date inputs follow the browser locale, which is why this component exists. */
const formatUsDate = (date: Date): string => `${pad2(date.getMonth() + 1)}/${pad2(date.getDate())}/${date.getFullYear()}`

const formatDate = (date: Date | null): string => {
    if (!date) return ''
    return `${String(date.getMonth() + 1).padStart(2, '0')} / ${String(date.getDate()).padStart(2, '0')} / ${date.getFullYear()}`
}

export const DatePicker = ({
    variant = 'primary',
    label,
    description,
    error,
    value: controlledValue,
    defaultValue = null,
    onChange,
    disabled = false,
    required = false,
    minDate,
    maxDate,
    typeable = false,
    placement = 'auto',
    style,
    'aria-label': ariaLabel,
    nativeCalendar = false,
    focusRef,
    onBlur,
}: DatePickerProps) => {
    const [internalValue, setInternalValue] = useState<Date | null>(defaultValue)
    const selected = controlledValue !== undefined ? controlledValue : internalValue
    // What onBlur reports: a change emitted in the same event (a typed segment flushed on blur) is not rendered yet.
    const latestRef = useRef(selected)
    latestRef.current = selected
    const [open, setOpen] = useState(false)
    const [popupPosition, setPopupPosition] = useState<{ top: number; left: number } | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const popupRef = useRef<HTMLDivElement>(null)
    const nativeRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(focusRef, () => ({ focus: () => containerRef.current?.querySelector<HTMLElement>(typeable ? '[role="group"]' : 'button')?.focus() }), [
        typeable,
    ])
    const isSecondary = variant === 'secondary'
    const labelId = useId()
    const [segments, setSegments] = useState<DateSegments>(() => toSegments(controlledValue ?? defaultValue))
    // onBlur reads the segments of the same event (a digit flushed on blur), before they are rendered.
    const segmentsRef = useRef(segments)
    segmentsRef.current = segments

    // A date that arrives from outside (a calendar pick, a reset) replaces the typed segments; a half-typed date stays as typed.
    const selectedTime = selected ? selected.getTime() : null
    useEffect(() => {
        if (!typeable) return
        const external = selectedTime === null ? null : new Date(selectedTime)
        setSegments((current) => (isSameDay(segmentsToDate(current), external) ? current : toSegments(external)))
    }, [typeable, selectedTime])

    const typedDate = typeable && isSegmentsComplete(segments) ? segmentsToDate(segments) : null
    const typedInvalid = typeable && isSegmentsComplete(segments) && !typedDate
    const typedOutOfRange = !!typedDate && isOutOfRange(typedDate, minDate, maxDate)
    const rangeError =
        minDate && maxDate
            ? `Enter a date between ${formatUsDate(minDate)} and ${formatUsDate(maxDate)}`
            : minDate
              ? `Enter a date on or after ${formatUsDate(minDate)}`
              : `Enter a date on or before ${maxDate ? formatUsDate(maxDate) : ''}`
    const shownError = error ?? (typedInvalid ? 'Enter a valid date' : typedOutOfRange ? rangeError : undefined)
    const hasError = !!shownError

    const handleSelect = useCallback(
        (date: Date) => {
            latestRef.current = date
            if (controlledValue === undefined) setInternalValue(date)
            onChange?.(date)
            setOpen(false)
        },
        [controlledValue, onChange]
    )

    const emit = (date: Date | null) => {
        latestRef.current = date
        if (controlledValue === undefined) setInternalValue(date)
        onChange?.(date)
    }

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            const target = e.target as Node
            if (!containerRef.current?.contains(target) && !popupRef.current?.contains(target)) setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    useEffect(() => {
        if (!open) {
            setPopupPosition(null)
            return
        }

        const updatePlacement = () => {
            const container = containerRef.current
            if (!container) return

            const { bottom, top, left } = container.getBoundingClientRect()
            const calendarHeight = 300
            const calendarWidth = 280
            const spacing = 8
            const shouldOpenAbove =
                placement === 'above' || (placement === 'auto' && bottom + calendarHeight + spacing > window.innerHeight && top > calendarHeight + spacing)
            const popupTop = shouldOpenAbove ? top - calendarHeight - spacing : bottom + spacing
            const popupLeft = Math.min(Math.max(8, left), window.innerWidth - calendarWidth - 8)
            setPopupPosition({ top: Math.max(8, popupTop), left: popupLeft })
        }

        // The popup is marked as an overlay, so a Modal or Drawer behind it sees it as the topmost one and keeps itself open: one Escape closes the
        // calendar only.
        const closeOnEscape = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || !isTopmostOverlay(popupRef.current)) return
            e.stopPropagation()
            setOpen(false)
        }

        updatePlacement()
        window.addEventListener('resize', updatePlacement)
        window.addEventListener('scroll', updatePlacement, true)
        document.addEventListener('keydown', closeOnEscape)
        return () => {
            window.removeEventListener('resize', updatePlacement)
            window.removeEventListener('scroll', updatePlacement, true)
            document.removeEventListener('keydown', closeOnEscape)
        }
    }, [open, placement])

    const displayValue = formatDate(selected ?? null)
    const calendarLabel = label ? `Pick ${label.toLowerCase()} from calendar` : 'Pick date from calendar'

    const openNativeCalendar = () => {
        // Focus the field first, as a native input does, so a pick is followed by a normal blur (save-on-blur forms rely on it).
        containerRef.current?.querySelector<HTMLElement>('[role="group"]')?.focus()
        try {
            nativeRef.current?.showPicker()
        } catch {
            nativeRef.current?.focus()
        }
    }

    const typedField = (suffix?: React.ReactNode) => (
        <DateField
            variant={variant}
            showTime={false}
            disabled={disabled}
            aria-labelledby={label ? labelId : undefined}
            aria-label={label ? undefined : ariaLabel}
            value={{ ...segments, hours: null, minutes: null, seconds: null, period: 'AM' }}
            onChange={({ month, day, year }) => {
                const next = { month, day, year }
                setSegments(next)
                segmentsRef.current = next
                // A typed day outside min/max is refused the same way as 02/31: the field shows why and the value is empty.
                const parsed = segmentsToDate(next)
                const date = parsed && !isOutOfRange(parsed, minDate, maxDate) ? parsed : null
                if (!isSameDay(date, selected)) emit(date)
            }}
            suffix={suffix}
        />
    )

    const triggerStateClasses = open ? 'ring-2 ring-accent' : hasError ? 'ring-1 ring-danger' : isSecondary ? '' : 'shadow-field'

    return (
        <div
            ref={containerRef}
            className={`relative flex flex-col gap-1 ${disabled ? 'opacity-50' : ''}`}
            style={style}
            onBlur={(e) => {
                if (!onBlur || containerRef.current?.contains(e.relatedTarget as Node)) return
                const typed = segmentsRef.current
                const accepted = segmentsToDate(typed)
                const incomplete = typeable && isSegmentsTouched(typed) && (!accepted || isOutOfRange(accepted, minDate, maxDate))
                onBlur(latestRef.current, { incomplete })
            }}
        >
            {label && (
                <label id={labelId} className={`text-sm font-medium ${hasError ? 'text-danger' : 'text-foreground'}`}>
                    {label}
                    {required && <span className="ml-0.5 text-danger">*</span>}
                </label>
            )}

            {typeable && nativeCalendar ? (
                <div className="relative">
                    {typedField(
                        <button
                            type="button"
                            tabIndex={-1}
                            disabled={disabled}
                            aria-label={calendarLabel}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={openNativeCalendar}
                            className="flex cursor-pointer items-center border-none bg-transparent p-0 text-foreground disabled:cursor-not-allowed"
                        >
                            <CalendarLucide size={16} strokeWidth={1.5} />
                        </button>
                    )}
                    {/* The browser's own calendar popup, anchored to the field. It is never shown or typed into; it only reports the picked day. */}
                    <input
                        ref={nativeRef}
                        type="date"
                        tabIndex={-1}
                        aria-hidden="true"
                        disabled={disabled}
                        value={selected ? formatDateYMD(selected) : ''}
                        min={minDate ? formatDateYMD(minDate) : undefined}
                        max={maxDate ? formatDateYMD(maxDate) : undefined}
                        onChange={(e) => emit(parseDateYMD(e.target.value))}
                        className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
                    />
                </div>
            ) : typeable ? (
                <div className="flex items-end gap-2">
                    <div className="min-w-0 flex-1">{typedField()}</div>
                    <Button
                        variant="secondary"
                        iconOnly
                        disabled={disabled}
                        aria-label={calendarLabel}
                        aria-expanded={open}
                        prefix={<CalendarLucide size={16} strokeWidth={1.5} />}
                        onClick={() => setOpen((v) => !v)}
                    />
                </div>
            ) : (
                <button
                    type="button"
                    disabled={disabled}
                    aria-labelledby={label ? `${labelId} ${labelId}-value` : undefined}
                    aria-label={label ? undefined : ariaLabel}
                    onClick={() => {
                        if (!disabled) setOpen((v) => !v)
                    }}
                    data-field-box
                    className={`flex h-9 w-full cursor-pointer items-center rounded-xl border-none p-0 text-left transition-shadow duration-150 disabled:cursor-not-allowed ${
                        isSecondary ? 'bg-default' : 'bg-surface'
                    } ${triggerStateClasses}`}
                >
                    <span
                        id={`${labelId}-value`}
                        className={`flex-1 px-3 text-sm font-normal leading-5 ${displayValue ? 'text-foreground' : 'text-foreground-muted'}`}
                    >
                        {displayValue || 'mm / dd / yyyy'}
                    </span>
                    <span className="flex items-center px-3 text-foreground-muted">
                        <CalendarLucide size={16} strokeWidth={1.5} />
                    </span>
                </button>
            )}

            {!hasError && description && <span className="text-xs text-foreground-muted">{description}</span>}
            {hasError && <span className="text-xs text-danger">{shownError}</span>}

            {open &&
                popupPosition &&
                createPortal(
                    <div
                        ref={popupRef}
                        data-ds2-overlay
                        className="fixed z-[10000] box-border w-[280px] rounded-3xl bg-surface p-4 shadow-overlay backdrop-blur-[20px]"
                        style={{ top: popupPosition.top, left: popupPosition.left }}
                    >
                        <Calendar value={selected} onChange={handleSelect} minDate={minDate} maxDate={maxDate} />
                    </div>,
                    document.body
                )}
        </div>
    )
}
